/**
 * Cloudflare Turnstile — invisible bot check for the screening endpoint.
 *
 * Env-gated: if REACT_APP_TURNSTILE_SITE_KEY is unset, this whole module
 * is a no-op and callers receive `null` tokens. The backend's verify is
 * also env-gated (TURNSTILE_SECRET_KEY), so both sides agree on "disabled"
 * without any special-case wiring.
 */

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
let widgetId = null;
// Only one token request may be in flight at a time — a second `execute()`
// call on the same widget without a `reset()` returns an error.
let pending = null;

export const turnstileEnabled = () => !!SITE_KEY;

function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script failed to load"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

async function waitForTurnstile(timeoutMs = 4000) {
  await loadScript();
  const start = Date.now();
  while (!window.turnstile && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 50));
  }
  if (!window.turnstile) throw new Error("turnstile global not available");
}

function ensureWidget() {
  if (widgetId != null) return;
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  // Off-screen but rendered — Turnstile decides internally whether the
  // visitor needs an interactive challenge; most humans never see it.
  host.style.cssText = "position:fixed;bottom:0;left:0;width:0;height:0;overflow:hidden;";
  document.body.appendChild(host);
  widgetId = window.turnstile.render(host, {
    sitekey: SITE_KEY,
    execution: "execute",
    appearance: "execute",
    callback: (token) => {
      if (pending) { pending.resolve(token); pending = null; }
    },
    "error-callback": () => {
      if (pending) { pending.reject(new Error("turnstile failed")); pending = null; }
    },
    "expired-callback": () => {
      if (pending) { pending.reject(new Error("turnstile expired")); pending = null; }
    },
  });
}

/**
 * Request a fresh Turnstile token. Returns `null` when the widget is not
 * configured (dev / pre-launch). Rejects if configured but the challenge
 * fails so callers can fall back to a "try again" toast.
 */
export async function getTurnstileToken() {
  if (!SITE_KEY) return null;
  await waitForTurnstile();
  ensureWidget();

  // Serialize concurrent requests — only one execute() at a time.
  if (pending) {
    try { await pending.promise; } catch (_) { /* prior failed, we'll retry */ }
  }

  return new Promise((resolve, reject) => {
    const p = {};
    p.promise = new Promise((res, rej) => { p.resolve = res; p.reject = rej; });
    pending = p;

    // Reset so a previously consumed/expired token slot is freed.
    try { window.turnstile.reset(widgetId); } catch (_) {}
    try { window.turnstile.execute(widgetId); }
    catch (e) { pending = null; return reject(e); }

    const timer = setTimeout(() => {
      if (pending === p) { pending = null; reject(new Error("turnstile timeout")); }
    }, 30_000);

    p.promise.then((t) => { clearTimeout(timer); resolve(t); },
                   (e) => { clearTimeout(timer); reject(e); });
  });
}
